import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-publications-card.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import fitty from 'fitty';
import { OutlineElement } from 'src/components/base/outline-element/outline-element';
import { LinkTargetType } from 'src/components/base/outline-link/config';
import { getUrlTarget } from '../../../utilities/url_utils';

export type AllowedColors =
  | 'gray'
  | 'light-blue'
  | 'blue'
  | 'green'
  | 'white'
  | 'transparent';

/**
 * The IDB Publications Card component.
 * @element idb-publications-card
 * @extends OutlineElement
 * @slot tags - the tags slot
 * @slot image - The image slot.
 * @slot summary - The summary slot.
 */
@customElement('idb-publications-card')
export class IdbPublicationsCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The color class to apply to the background.
   */
  @property({ type: String, attribute: 'card-background' })
  cardBackground: AllowedColors = 'gray';

  /**
   * The link to the publication.
   */
  @property({ type: String, reflect: true, attribute: 'link' })
  link: string;

  /**
   * If true, the entire card link is clickable.
   * @attr entire-link-clickable
   */
  @property({ type: Boolean, attribute: 'entire-link-clickable' })
  entireLinkClickable = false;

  /**
   * The link target attribute
   */
  @property({ type: String, reflect: true, attribute: 'link-target' })
  linkTarget: LinkTargetType;

  /**
   * The title of the publication.
   */
  @property({ type: String, attribute: 'title' })
  title: string;

  /**
   * The publication date label of the card.
   */
  @property({ type: String, attribute: 'date-label' })
  dateLabel = 'Date';

  /**
   * The publication date of the card.
   */
  @property({ type: String, attribute: 'date' })
  date: string;

  /**
   * The authors of the publication.
   */
  @property({ type: String, attribute: 'authors' })
  authors: string;

  /**
   * The text to display on the button of the card.
   * @attr button-text
   */
  @property({ type: String, attribute: 'button-text' })
  buttonText: string;

  @property({ type: Boolean, attribute: 'data-label-bold' })
  dataLabelBold = false;

  connectedCallback() {
    super.connectedCallback();
  }

  firstUpdated(): void {
    const fitText = (this.shadowRoot as ShadowRoot).querySelector(
      '.fitty-wrapper'
    );
    if (fitText !== null) {
      fitty(fitText as HTMLElement, {
        minSize: 20,
        maxSize: 60,
        multiLine: true,
      });
    }
  }

  render(): TemplateResult {
    this.linkTarget = getUrlTarget(this.link, this.linkTarget);
    return html`
      ${
        this.entireLinkClickable
          ? html`
        <a href="${this.link}" target="${
              this.linkTarget
            }" class="entire-link-wrapper">
          ${this.cardTemplate()}
        </a>
      `
          : html`
        ${this.cardTemplate()}
      `
      }
    `;
  }

  cardTemplate(): TemplateResult {
    const cardClasses = {
      card: true,
      [`outline-bg-${this.cardBackground}`]: this.cardBackground !== undefined,
    };

    return html`
      <div class="${classMap(cardClasses)}">

        <div class="image-wrapper">
          <slot name="image"></slot>
        </div>  
        
        <div class="content-top-wrapper">
          
          <div class="tag-wrapper">
            <slot name="tags"></slot>
          </div>

          <div class="title">
            <idb-heading level-size="xl" level-style="semibold" level="h3" margin-size="0px">
              ${this.title}
            </idb-heading>
          </div>
          
        </div>

        <div class="content-bottom-wrapper">
          <div class="summary-wrapper">
            <slot name="summary"></slot>
          </div>

          ${this.dateTemplate()}
          ${this.authorsTemplate()}
          ${this.buttonTemplate()}
        </div>
      </div>
    `;
  }

  dateTemplate(): TemplateResult | null {
    if (!this.date) return null;

    return html`
      <div class="date">
        <idb-label-data-heading 
          classes=${`data-text-gray ${
            this.dataLabelBold ? 'data-text-bold label-text-bold' : null
          }`}>
          <div slot="heading-label">${this.dateLabel || 'Date'}</div>
          <div slot="heading-data">${this.date}</div>
        </idb-label-data-heading>
      </div>
    `;
  }

  authorsTemplate(): TemplateResult | null {
    if (!this.authors) return null;

    return html`
      <div class="authors">
        <idb-label-data-heading label="Authors" data="${
          this.authors
        }" use-properties 
        classes=${`data-text-gray ${
          this.dataLabelBold ? 'data-text-bold label-text-bold' : null
        }`}>
        </idb-label-data-heading>
      </div>
    `;
  }

  buttonTemplate(): TemplateResult | null {
    if (!this.buttonText) return null;

    return html`
      <div class="button-wrapper">
        <idb-button
          button-style="primary"
          button-size="small"
          button-url=${this.link}
          button-target="${this.linkTarget}">
          ${this.buttonText}
        </idb-button>
      </div>
    `;
  }
}
